// 创建果壳视频点位
import { useContext, useEffect, useMemo } from "react"
import { CalendarContext, SelectDateRange } from "Components/Calendar/context"
import { Board, BoardModifyDialog } from "Types/board"
import { timeFormatStr } from "Methods/basic"
import { useAddVideoBoard, useUpdateVideoBoard } from "Hooks/board"
import { useAssign, useInputChange } from "Hooks/basic"
import { AppContext } from "Views/Home/context"
import moment from "moment"

// 多条预约时的时间范围生成工具
export function useCreateModalTimeStr(selectDateRange: SelectDateRange | null) {
    return useMemo(() => {
        let modalTimeStr = ""
        const adTimes: number[] = []
        if (!selectDateRange) {
            return {
                adTimes: [],
                modalTimeStr: "",
            }
        }
        const dateRange = selectDateRange as SelectDateRange
        const startTime = Math.min(dateRange.start.getTime(), dateRange.end.getTime())
        const endTime = Math.max(dateRange.start.getTime(), dateRange.end.getTime())
        const startDate = new Date(startTime)
        const endDate = new Date(endTime)
        const startDay = startDate.getDay() || 7
        const endDay = endDate.getDay() || 7
        if (startTime === endTime) {
            modalTimeStr = `${moment(startDate).format(timeFormatStr.month)}`
        } else {
            modalTimeStr = `${moment(startDate).format(timeFormatStr.month)} - ${moment(endDate).format(timeFormatStr.month)}`
        }
        for (let i = 0; i <= endDay - startDay; i++) {
            adTimes.push(startTime + (i * 1000 * 3600 * 24))
        }
        return { modalTimeStr, adTimes }
    }, [selectDateRange])
}

type CreateType = "single" | "multiple"
export function useCreate(type: CreateType) {
    const initialExternal = {}
    const {
        object: external,
        handleChange: setExternals,
    } = useAssign(initialExternal)
    const {
        boardModifyDialog, handleChangeBoardModifyDialog,
        selectPlaceId, selectDateRange, resetPlaceId,
    } = useContext(CalendarContext)
    const { roleMap } = useContext(AppContext)
    const { isBusiness } = roleMap
    const dialogData = boardModifyDialog as BoardModifyDialog

    const value = useCreateModalTimeStr(selectDateRange)
    const adTime = dialogData.date.getTime()
    const modalTimeStr = moment(dialogData.date).format(timeFormatStr.all)

    const { text, handleChange, clearText } = useInputChange("")
    const { text: projectId, handleChange: handleProjectIdChange, clearText: clearProjectId, setText: setProjectId } = useInputChange("")
    const { text: projectMoney, handleChange: handleProjectMoneyChange, clearText: clearProjectMoney, setText: setProjectMoney } = useInputChange("")
    const { add } = useAddVideoBoard()
    const callback = async () => {
        await add({
            name: text,
            ad_time: adTime,
            seq_id: dialogData.place.seq_num,
            ad_place_id: dialogData.place._id,
            project_id: projectId || '',
            project_money: projectMoney || '',
        })
    }
    const cancelCallback = () => {
        handleChangeBoardModifyDialog(null)
        clearText()
        resetPlaceId()
    }
    const disabled = text === "" || (isBusiness && (projectId.length > 50 || projectMoney.length > 50 || projectId === ""))
    return {
        callback, cancelCallback,
        dialogData, disabled, modalTimeStr, isBusiness,
        text, handleChange,
        projectId, handleProjectIdChange,
        projectMoney, handleProjectMoneyChange,
    }
}
// 修改果壳视频点位
export function useUpdate() {
    const {
        boardModifyDialog, handleChangeBoardModifyDialog,
    } = useContext(CalendarContext)
    const dialogData = boardModifyDialog as BoardModifyDialog
    const board = dialogData.board as Board
    const isPass = dialogData.isPass
    const { roleMap, user } = useContext(AppContext)
    const { isBusiness } = roleMap
    const isMine = board.creator_id === user.uid
    const { text, handleChange, clearText, setText } = useInputChange("")
    const { text: projectId, handleChange: handleProjectIdChange, clearText: clearProjectId, setText: setProjectId } = useInputChange("")
    const { text: projectMoney, handleChange: handleProjectMoneyChange, clearText: clearProjectMoney, setText: setProjectMoney } = useInputChange("")
    const initialExternal = {}
    const {
        object: external,
        handleChange: setExternals,
    } = useAssign(initialExternal)
    useEffect(() => {
        setText(board.name)
        setProjectId(board.project_id)
        setProjectMoney(board.project_money)
    }, [board, setText, setProjectId, setProjectMoney])
    const { update } = useUpdateVideoBoard()
    const callback = async () => {
        await update({
            variables: {
                id: board.id,
                name: text,
                project_id: projectId || '',
                project_money: projectMoney || '',
            },
        })
    }
    const cancelCallback = () => {
        handleChangeBoardModifyDialog(null)
        clearText()
        clearProjectId()
        clearProjectMoney()
    }
    const dateStr = moment(dialogData.date).format(timeFormatStr.all)
    const disabled = text === "" || (isBusiness && (projectId.length > 50 || projectMoney.length > 50 || projectId === ""))
    return {
        callback, cancelCallback,
        dialogData, disabled, dateStr,
        text, handleChange, external,
        isBusiness, isMine, isPass,
        projectMoney, handleProjectMoneyChange,
        projectId, handleProjectIdChange,
    }
}
